import type { InputCmd } from "../net/input.ts";
import type { DamageInfo } from "../sim/combat.ts";
import type { MoveMods } from "../sim/movement.ts";
import type { Sim } from "../sim/sim.ts";
import type { HeroDef, HeroId, Slot } from "./types.ts";
import type { ProjectileKind, ZoneKind } from "./kitlib.ts";

export interface Kit {
  init(sim: Sim, eid: number): void;
  tick(sim: Sim, eid: number, input: InputCmd, pressed: number, canAct: boolean): void;
  movement?(sim: Sim, eid: number, input: InputCmd, mods: MoveMods): void;
  /** Handles MoveKind.Hero..31 scripted movement while Move.ticks is active. */
  scripted?(sim: Sim, eid: number, input: InputCmd, kind: number): void;
  /** Target-side damage interception after shared filters and before health pools. */
  onDamaged?(sim: Sim, eid: number, amount: number, attacker: number | undefined, info: DamageInfo): number;
  /** Return true to prevent a lethal hit; the kit must leave the player in a valid alive state. */
  onLethal?(sim: Sim, eid: number, attacker: number | undefined): boolean;
  /** Death notification after KO credit is assigned. */
  onDeath?(sim: Sim, eid: number, killer: number | undefined): void;
  /** Preferred damage-dealt hook; `dealtDamage` is kept for existing modules. */
  onDealtDamage?(sim: Sim, eid: number, target: number, amount: number, info: DamageInfo): void;
  dealtDamage?(sim: Sim, attacker: number, target: number, dealt: number): void;
  /** Called on the target after healing lands. */
  onHealed?(sim: Sim, eid: number, healer: number, amount: number): void;
  /** Called on the healer after healing lands. */
  onDealtHeal?(sim: Sim, eid: number, target: number, amount: number): void;
}

/**
 * An aimed ability, Rivals style: a preview follows the crosshair, LMB confirms and RMB cancels.
 * "press" targets start when the slot's key is pressed, and confirming delivers that press to the
 * kit, which places the thing at `targetPoint`. "kit" targets are the second step of an ability
 * already under way (leap, then pick where to slam): the kit calls `beginTargeting` and checks
 * `targetConfirmed`. Bots quick-cast: a press is delivered at once, a kit step confirms on fire.
 */
export interface TargetSpec {
  /** Farthest target along the crosshair (as `groundAimPoint`). */
  range: number;
  /** Metres the point backs away from a wall it's aimed into. */
  clearance?: number;
  /** The preview's ground ring radius. */
  radius?: number;
  /** The construct kind about to be placed, previewed at the point facing the caster's view. */
  construct?: number;
  /** Default "press". */
  start?: "press" | "kit";
  /** The target is the caster's own position (surfacing from a burrow), not the crosshair. */
  self?: boolean;
  /** "kit" targets confirm by themselves after this many seconds. */
  timeout?: number;
  /** HUD verb for LMB, e.g. "Place" or "Slam". */
  verb?: string;
}

export interface BarrierSpec {
  upVar: number;
  hpVar: number;
  maxHp: number;
  width: number;
  height: number;
  forward: number;
  regen?: number;
  regenDelay?: number;
}

export type ConstructShape = { kind: "box"; hw: number; hd: number; h: number } | { kind: "cyl"; r: number; h: number };

export interface ConstructKind {
  maxHp: number;
  seconds: number;
  shape: ConstructShape;
  solid?: boolean;
  /** Seconds before a solid construct becomes a movement obstacle; defaults to 0.25 for solid kinds. */
  solidAfter?: number;
  blocksShots?: boolean;
  /** Defaults to true. */
  damageable?: boolean;
  /** Defaults to false: allied damage is ignored and allied shots do not target it. */
  friendlyDamage?: boolean;
  heals?: boolean;
  /** Defaults to false: owner death despawns the construct. */
  persistOnOwnerDeath?: boolean;
  tick?(sim: Sim, e: number): void;
  onDamage?(sim: Sim, e: number, amount: number, attacker: number | undefined): void;
  onDestroy?(sim: Sim, e: number, reason: "expired" | "killed" | "owner" | "manual"): void;
  visual: { color: string; shape?: ConstructShape["kind"] };
}

export interface SpawnConstructOpts {
  hp?: number;
  seconds?: number;
  v0?: number;
  ownerDeath?: "despawn" | "persist";
}

export interface HeroModule {
  def: HeroDef;
  kit: Kit;
  projectiles?: Record<number, ProjectileKind>;
  zones?: Record<number, ZoneKind>;
  constructs?: Record<number, ConstructKind>;
  fx?: Record<number, { color: string }>;
  barrier?: BarrierSpec;
  targets?: Partial<Record<Slot, TargetSpec>>;
}

export const pk = (hero: HeroId, i: number) => {
  if (i < 0 || i >= 8) throw new Error("projectile kind index out of range");
  return 1 + hero * 8 + i;
};
export const zk = (hero: HeroId, i: number) => {
  if (i < 0 || i >= 4) throw new Error("zone kind index out of range");
  return 1 + hero * 4 + i;
};
export const ck = (hero: HeroId, i: number) => {
  if (i < 0 || i >= 4) throw new Error("construct kind index out of range");
  return 1 + hero * 4 + i;
};
export const fxk = (hero: HeroId, i: number) => {
  if (i < 0 || i >= 8) throw new Error("fx kind index out of range");
  return 16 + hero * 8 + i;
};
